import type { KeyboardEvent } from "react";
import type { AnnotatedCycleStage } from "./salesCycleDue";
import { dueUrgencyLabel } from "./salesCycleDue";
import { ACTION_HINTS } from "../../help/fieldHints";

const CX = 240;
const CY = 240;
const INNER = 98;
const OUTER = 186;
/** Labels sit clearly outside the ring; keep ahead of longest two-line names. */
const LABEL_R = 238;
const GAP_DEG = 2.8;
/** Horizontal gap either side of the sector centre when a completed stage shows number + check. */
const CHECK_OFFSET = 14;

function polar(radius: number, angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: CX + radius * Math.cos(rad), y: CY + radius * Math.sin(rad) };
}

function annularSector(startDeg: number, endDeg: number) {
  const large = endDeg - startDeg > 180 ? 1 : 0;
  const outerStart = polar(OUTER, startDeg);
  const outerEnd = polar(OUTER, endDeg);
  const innerEnd = polar(INNER, endDeg);
  const innerStart = polar(INNER, startDeg);
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${OUTER} ${OUTER} 0 ${large} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${INNER} ${INNER} 0 ${large} 0 ${innerStart.x} ${innerStart.y}`,
    "Z",
  ].join(" ");
}

function wrapLabel(name: string): string[] {
  const parts = name.split(" ");
  if (parts.length < 2 || name.length < 14) return [name];
  const mid = Math.ceil(parts.length / 2);
  return [parts.slice(0, mid).join(" "), parts.slice(mid).join(" ")];
}

/** Anchor text away from the ring using the label's radial direction. */
export function labelAnchor(angleDeg: number): "start" | "middle" | "end" {
  const rad = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  if (cos > 0.35) return "start";
  if (cos < -0.35) return "end";
  return "middle";
}

export function SalesCycleDiagram({
  stages,
  selectedId,
  onSelect,
}: {
  stages: AnnotatedCycleStage[];
  selectedId: string | null;
  onSelect: (stageId: string) => void;
}) {
  const slice = 360 / Math.max(stages.length, 1);

  function onSectorKeyDown(event: KeyboardEvent<SVGGElement>, stageId: string) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(stageId);
    }
  }

  return (
    <svg
      className="crm-cycle-diagram"
      viewBox="-48 -48 576 576"
      role="group"
      aria-label="Sales cycle stages"
    >
      <circle className="crm-cycle-hub-ring" cx={CX} cy={CY} r={INNER - 12} />
      <text className="crm-cycle-hub-title" x={CX} y={CY - 8} textAnchor="middle">
        Sales cycle
      </text>
      <text className="crm-cycle-hub-caption" x={CX} y={CY + 16} textAnchor="middle">
        {stages.length} {stages.length === 1 ? "stage" : "stages"}
      </text>
      {stages.map((stage, index) => {
        const start = -90 + index * slice + GAP_DEG / 2;
        const end = -90 + (index + 1) * slice - GAP_DEG / 2;
        const mid = (start + end) / 2;
        const label = polar(LABEL_R, mid);
        const number = polar((INNER + OUTER) / 2, mid);
        const lines = wrapLabel(stage.name);
        const firstDy = -((lines.length - 1) * 1.15) / 2;
        const selected = stage.id === selectedId;
        const health = dueUrgencyLabel(stage.urgency);
        const stateNote = stage.complete
          ? "Complete"
          : stage.active
            ? health
            : "Upcoming — not yet active";
        const className = [
          "crm-cycle-sector",
          `is-due-${stage.urgency}`,
          stage.active ? "is-active" : "is-inactive",
          selected ? "is-selected" : "",
          stage.complete ? "is-complete" : "",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <g
            key={stage.id}
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            aria-label={`${stage.index + 1}. ${stage.name}${stage.active ? "" : ", upcoming"}`}
            data-due-urgency={stage.urgency}
            data-stage-active={stage.active ? "true" : "false"}
            onClick={() => onSelect(stage.id)}
            onKeyDown={(event) => onSectorKeyDown(event, stage.id)}
          >
            <title>{`${stage.index + 1}. ${stage.name} — ${stateNote}. ${ACTION_HINTS.map_sector}`}</title>
            <path className={className} d={annularSector(start, end)} />
            <text
              className="crm-cycle-sector-number"
              x={stage.complete ? number.x - CHECK_OFFSET : number.x}
              y={number.y}
              textAnchor="middle"
              dy="0.35em"
            >
              {index + 1}
            </text>
            {stage.complete && (
              <text
                className="crm-cycle-sector-check"
                x={number.x + CHECK_OFFSET}
                y={number.y}
                textAnchor="middle"
                dy="0.35em"
              >
                ✓
              </text>
            )}
            <text
              className={stage.active ? "crm-cycle-sector-label" : "crm-cycle-sector-label is-inactive"}
              x={label.x}
              y={label.y}
              textAnchor={labelAnchor(mid)}
              dominantBaseline="middle"
            >
              {lines.map((line, lineIndex) => (
                <tspan key={line} x={label.x} dy={lineIndex === 0 ? `${firstDy}em` : "1.15em"}>
                  {line}
                </tspan>
              ))}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
